import { useEffect, useMemo, useState } from "react";
import { api, Expense } from "../api";
import { formatVND, formatDate } from "../utils";
import { GlassPanel, KpiCard, Badge, Modal, toast, confirm, SkeletonTable, ErrorState, DataTable, EmptyState } from "../components";
import type { Column } from "../components";

const CATEGORIES = [
  { value: "mat_bang", label: "Mặt bằng" },
  { value: "dien", label: "Điện" },
  { value: "nuoc", label: "Nước" },
  { value: "luong", label: "Lương" },
  { value: "ship", label: "Vận chuyển" },
  { value: "khac", label: "Khác" },
];

const catLabel = (val: string) => CATEGORIES.find((c) => c.value === val)?.label || val;

type ExpenseForm = {
  expense_date: string;
  category: string;
  description: string;
  amount: number;
  attachment_url: string;
};

const emptyForm = (): ExpenseForm => ({
  expense_date: new Date().toISOString().slice(0, 10),
  category: "khac",
  description: "",
  amount: 0,
  attachment_url: "",
});

/** Trang Chi phí (P0-6) — FULL */
export default function ExpensesPage() {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().slice(0, 10);
  });
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));
  const [catFilter, setCatFilter] = useState("");
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
  const advancedFilterActive = catFilter !== "";
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState<ExpenseForm>(emptyForm());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadExpenses();
  }, [fromDate, toDate, catFilter]);

  const loadExpenses = async () => {
    setLoading(true);
    try {
      const r = await api.expenses.list({
        from: fromDate,
        to: toDate,
        category: catFilter || undefined,
        limit: 200,
      });
      setExpenses(r.expenses || []);
    } catch (e: unknown) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const totalAmount = expenses.reduce((s, e) => s + e.amount, 0);
  const byCategory = expenses.reduce<Record<string, number>>((acc, e) => {
    acc[e.category] = (acc[e.category] || 0) + e.amount;
    return acc;
  }, {});
  const topCategories = Object.entries(byCategory).sort((a, b) => b[1] - a[1]);

  const expenseColumns: Column<Expense>[] = useMemo(() => [
    {
      key: "expense_date",
      header: "Ngày",
      sortable: true,
      render: (e) => formatDate(e.expense_date),
    },
    {
      key: "category",
      header: "Nhóm",
      render: (e) => <Badge variant="info">{catLabel(e.category)}</Badge>,
    },
    {
      key: "description",
      header: "Mô tả",
      render: (e) => (
        <>
          {e.description || "—"}
          {e.attachment_url && (
            <a href={e.attachment_url} target="_blank" rel="noreferrer" style={{ marginLeft: 6, fontSize: 11, color: "var(--accent-blue)" }}>
              📎
            </a>
          )}
        </>
      ),
    },
    {
      key: "amount",
      header: "Số tiền",
      sortable: true,
      align: "right",
      render: (e) => <span style={{ fontWeight: 700, color: "var(--accent-red)" }}>{formatVND(e.amount)}</span>,
    },
    {
      key: "_actions",
      header: "",
      render: (e) => (
        <div style={{ display: "flex", gap: 4 }}>
          <button className="btn btn-ghost" style={{ fontSize: 11, padding: "2px 6px" }} onClick={() => handleEdit(e)} title="Sửa">
            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>edit</span>
          </button>
          <button className="btn btn-ghost" style={{ fontSize: 11, padding: "2px 6px", color: "var(--accent-red)" }} onClick={() => handleDelete(e.id)} title="Xóa">
            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>delete</span>
          </button>
        </div>
      ),
    },
  ], []);

  const handleSubmit = async () => {
    if (!form.description.trim() || form.amount <= 0) {
      toast.error("Nhập mô tả và số tiền > 0");
      return;
    }
    setSubmitting(true);
    try {
      if (editId) {
        await api.expenses.update(editId, {
          expense_date: form.expense_date,
          category: form.category,
          amount: form.amount,
          description: form.description,
          attachment_url: form.attachment_url || undefined,
        });
      } else {
        await api.expenses.create({
          expense_date: form.expense_date,
          category: form.category,
          amount: form.amount,
          description: form.description,
          attachment_url: form.attachment_url || undefined,
        });
      }
      setShowModal(false);
      setEditId(null);
      setForm(emptyForm());
      await loadExpenses();
    } catch (e: unknown) {
      toast.error((e as Error).message || "Lỗi lưu chi phí");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (exp: Expense) => {
    setEditId(exp.id);
    setForm({
      expense_date: exp.expense_date,
      category: exp.category,
      description: exp.description || "",
      amount: exp.amount,
      attachment_url: exp.attachment_url || "",
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!await confirm({ title: "Xoá chi phí", message: "Xoá chi phí này?", danger: true, confirmLabel: "Xoá" })) return;
    try {
      await api.expenses.delete(id);
      await loadExpenses();
    } catch (e: unknown) {
      toast.error((e as Error).message);
    }
  };

  return (
    <>
      <div className="kpi-grid">
        <KpiCard label="Tổng chi tháng" value={formatVND(totalAmount)} dotColor="bad" note={`${fromDate} → ${toDate}`} />
        <KpiCard label="Số khoản" value={expenses.length.toString()} dotColor="blue" note="trong kỳ" />
        {CATEGORIES.map((cat) => {
          const amt = byCategory[cat.value];
          if (!amt) return null;
          return (
            <KpiCard
              key={cat.value}
              label={cat.label}
              value={formatVND(amt)}
              dotColor={cat.value === "luong" ? "warn" : cat.value === "mat_bang" ? "bad" : "blue"}
              note={`${((amt / Math.max(totalAmount, 1)) * 100).toFixed(0)}%`}
            />
          );
        }).filter(Boolean).slice(0, 4)}
      </div>

      <GlassPanel title="Chi phí" badge={`${expenses.length} khoản`}>
        <div className="filter-bar">
          <button className="btn" onClick={() => { setForm(emptyForm()); setEditId(null); setShowModal(true); }} style={{ flex: 1 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>add</span>
            Thêm chi phí
          </button>
          <button
            className={`btn-icon ${advancedFilterActive ? "active" : ""}`}
            onClick={() => setShowAdvancedFilter((v) => !v)}
            title="Bộ lọc"
            aria-label="Bộ lọc"
            style={advancedFilterActive ? { background: "var(--accent-blue)", color: "#fff" } : undefined}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>tune</span>
          </button>
        </div>

        {showAdvancedFilter && (
          <div className="filter-advanced">
            <div className="filter-field">
              <label>Từ ngày</label>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </div>
            <div className="filter-field">
              <label>Đến ngày</label>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </div>
            <div className="filter-field">
              <label>Nhóm chi phí</label>
              <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)}>
                <option value="">Tất cả nhóm</option>
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            {advancedFilterActive && (
              <button className="btn btn-secondary" onClick={() => setCatFilter("")}>
                Xoá lọc
              </button>
            )}
          </div>
        )}

        {loading ? (
          <SkeletonTable rows={6} cols={5} />
        ) : error ? (
          <ErrorState error={error} onRetry={loadExpenses} />
        ) : expenses.length === 0 ? (
          <EmptyState icon="payments" title="Không có chi phí" description="Không có chi phí nào trong kỳ này." />
        ) : (
          <DataTable
            columns={expenseColumns}
            rows={expenses}
            rowKey={(e) => String(e.id)}
            pageSize={50}
            defaultSort={{ key: "expense_date", dir: "desc" }}
          />
        )}
      </GlassPanel>

      {/* Bar chart phân bổ theo category */}
      {topCategories.length > 0 && (
        <GlassPanel title="Phân bổ theo nhóm" badge={`${topCategories.length} nhóm`}>
          <div className="cat-bars">
            {topCategories.map(([cat, amount]) => {
              const max = topCategories[0][1];
              return (
                <div className="cat-row" key={cat}>
                  <div className="cat-row-head">
                    <span className="cat-name">{catLabel(cat)}</span>
                    <span className="cat-amount">{formatVND(amount)}</span>
                  </div>
                  <div className="cat-bar-track">
                    <div
                      className="cat-bar-fill"
                      style={{
                        width: `${Math.max(2, (amount / Math.max(max, 1)) * 100)}%`,
                        background: cat === "mat_bang" ? "var(--accent-red)" :
                          cat === "luong" ? "var(--accent-yellow)" :
                          cat === "dien" ? "var(--accent-blue)" :
                          "var(--accent-emerald)",
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </GlassPanel>
      )}

      {/* Modal thêm/sửa chi phí */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editId ? "Sửa chi phí" : "Thêm chi phí"}
        footer={
          <button className="btn" onClick={handleSubmit} disabled={submitting}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>save</span>
            {submitting ? "Đang lưu…" : editId ? "Cập nhật" : "Lưu chi phí"}
          </button>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Ngày *</label>
          <input type="date" value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} />

          <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Nhóm chi phí *</label>
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>

          <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Mô tả *</label>
          <input placeholder="Mô tả chi phí" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />

          <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Số tiền *</label>
          <input type="number" placeholder="0" value={form.amount || ""} onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })} />

          <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Đính kèm (URL)</label>
          <input placeholder="https://..." value={form.attachment_url} onChange={(e) => setForm({ ...form, attachment_url: e.target.value })} />
        </div>
      </Modal>
    </>
  );
}
